'use client';

import type { CmsWorkspaceLaunchContext } from '@/cms/workspace/launch-context';
import { onFirstPageInteraction } from '@/lib/ui/first-page-interaction';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkspaceRuntimeDialogCallbacks } from './workspace-1/contracts/dialog-callbacks';
import type { WorkspaceRuntimeActionCallbacks } from './workspace-1/contracts/dialog-callbacks';
import type { WorkspaceStudioRenderModel } from './workspace-1/contracts/server-model';
import { WorkspacePersistentFrame } from './workspace-1/shell/workspace-persistent-frame';
import { WorkspaceStudioSkeleton } from './workspace-1/shell/workspace-studio-skeleton';
import {
  type WorkspaceLoadErrorCopy,
  useWorkspaceLoadErrorToast,
} from './workspace-load-error';
import {
  type CmsWorkspaceActivation,
  type CmsWorkspaceLaunchDetail,
  subscribeCmsWorkspaceActivation,
  takeCurrentCmsWorkspaceActivation,
} from './workspace-session-controller';

type ClientLoadedWorkspaceComponent = typeof import(
  './client-loaded-workspace'
).ClientLoadedWorkspace;

type ClientLoadedWorkspaceModule = {
  ClientLoadedWorkspace: ClientLoadedWorkspaceComponent;
  preloadClientWorkspaceRuntime: (locale: string) => Promise<void>;
};

type WorkspaceHostState = 'idle' | 'loading' | 'ready' | 'error';

export function ClientLoadedWorkspaceHost({
  initialLaunchDetail: configuredInitialLaunchDetail,
  errorCopy,
  launchContext,
  locale,
  runtimeDialogCallbacks,
  runtimeActionCallbacks,
  shellHero,
  surface,
}: {
  initialLaunchDetail?: CmsWorkspaceLaunchDetail;
  errorCopy: WorkspaceLoadErrorCopy;
  launchContext?: CmsWorkspaceLaunchContext;
  locale: string;
  runtimeDialogCallbacks?: WorkspaceRuntimeDialogCallbacks;
  runtimeActionCallbacks?: WorkspaceRuntimeActionCallbacks;
  shellHero?: WorkspaceStudioRenderModel['shell']['hero'];
  surface: WorkspaceStudioRenderModel['surface'];
}) {
  const [ClientLoadedWorkspace, setClientLoadedWorkspace] =
    useState<ClientLoadedWorkspaceComponent | null>(null);
  const [workspaceState, setWorkspaceState] = useState<WorkspaceHostState>(
    configuredInitialLaunchDetail ? 'loading' : 'idle'
  );
  const [initialLaunchDetail, setInitialLaunchDetail] =
    useState<CmsWorkspaceLaunchDetail | null>(
      configuredInitialLaunchDetail ?? null
    );
  const [retryNonce, setRetryNonce] = useState(0);
  const configuredInitialLaunchDetailRef = useRef(
    configuredInitialLaunchDetail
  );
  const clientPromiseRef = useRef<Promise<ClientLoadedWorkspaceModule> | null>(
    null
  );

  const loadClientWorkspace = useCallback(() => {
    if (clientPromiseRef.current) {
      return clientPromiseRef.current;
    }

    clientPromiseRef.current = import(
      /* webpackFetchPriority: "high" */ './client-loaded-workspace'
    )
      .then((module) => {
        setClientLoadedWorkspace(() => module.ClientLoadedWorkspace);
        return module;
      })
      .catch((error) => {
        clientPromiseRef.current = null;
        throw error;
      });

    return clientPromiseRef.current;
  }, []);

  const handleWorkspaceError = useCallback(() => {
    setWorkspaceState('error');
  }, []);

  const activateWorkspace = useCallback(
    (detail: CmsWorkspaceLaunchDetail | null = null) => {
      setInitialLaunchDetail(detail);
      setWorkspaceState((currentState) =>
        currentState === 'ready' ? 'ready' : 'loading'
      );
      void loadClientWorkspace()
        .then((module) => module.preloadClientWorkspaceRuntime(locale))
        .catch(handleWorkspaceError);
    },
    [handleWorkspaceError, loadClientWorkspace, locale]
  );
  const handleWorkspaceActivation = useCallback(
    (activation: CmsWorkspaceActivation) => {
      takeCurrentCmsWorkspaceActivation();
      activateWorkspace(activation.detail);
    },
    [activateWorkspace]
  );

  useEffect(() => {
    return subscribeCmsWorkspaceActivation(handleWorkspaceActivation);
  }, [handleWorkspaceActivation]);

  useEffect(() => {
    const queuedActivation = takeCurrentCmsWorkspaceActivation();
    const detail =
      queuedActivation?.detail ?? configuredInitialLaunchDetailRef.current;
    if (detail) {
      const requestedToolId =
        surface === 'workspace-page'
          ? new URL(window.location.href).searchParams.get('tool')
          : null;
      activateWorkspace(
        requestedToolId && requestedToolId !== detail.defaultToolId
          ? {
              ...detail,
              defaultToolId: requestedToolId,
              launchId: `${detail.launchId}:${requestedToolId}`,
            }
          : detail
      );
    }
  }, [activateWorkspace, surface]);

  useEffect(() => {
    return onFirstPageInteraction(() => {
      void loadClientWorkspace()
        .then((module) => module.preloadClientWorkspaceRuntime(locale))
        .catch((error) => {
          console.warn(
            '[cms-workspace-host] Failed to preload workspace runtime',
            error
          );
        });
    });
  }, [loadClientWorkspace, locale]);

  const handleWorkspaceReady = useCallback(() => {
    setWorkspaceState('ready');
  }, []);
  const retryWorkspaceLoad = useCallback(() => {
    setWorkspaceState('loading');
    setRetryNonce((current) => current + 1);
    void loadClientWorkspace().catch(handleWorkspaceError);
  }, [handleWorkspaceError, loadClientWorkspace]);

  useWorkspaceLoadErrorToast({
    errorCopy,
    hasError: workspaceState === 'error',
    onRetry: retryWorkspaceLoad,
  });

  if (workspaceState === 'idle') {
    return null;
  }

  const shouldShowWorkspace = workspaceState === 'ready';
  const runtime = ClientLoadedWorkspace ? (
    <ClientLoadedWorkspace
      key={retryNonce}
      initialLaunchDetail={initialLaunchDetail}
      launchContext={launchContext}
      locale={locale}
      onError={handleWorkspaceError}
      onReady={handleWorkspaceReady}
      renderWorkspace={shouldShowWorkspace}
      runtimeDialogCallbacks={runtimeDialogCallbacks}
      runtimeActionCallbacks={runtimeActionCallbacks}
      shellHero={shellHero}
      surface={surface}
    />
  ) : null;

  return shellHero ? (
    <WorkspacePersistentFrame hero={shellHero}>
      {runtime}
    </WorkspacePersistentFrame>
  ) : (
    <>
      {shouldShowWorkspace ? null : <WorkspaceStudioSkeleton />}
      {runtime}
    </>
  );
}
